import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { ActivityIndicator, StyleSheet, Text as RNText, View } from 'react-native';

import { FoldTicks } from '@/components/print/fold-ticks';
import {
	INVOICE_SHEET_SIDE_PAD_IN, INVOICE_SHEET_TOP_IN, PRINT_BOX_ROW_PAD, PRINT_FONT, PRINT_LINE, PRINT_ROW_HEIGHT, TO_ADDRESS_INDENT, TO_ROW_GAP, headerMinHeight,
} from '@/components/print/print-type';
import { useAuth } from '@/hooks/use-auth';
import { accountingService, type InvoicePrintBundle } from '@/services/accounting-service';
import { formatBusinessDateUS } from '@/utils/date-utils';
import { invoiceCopy } from '@/utils/document-copy';
import { formatMoneyCents } from '@/utils/format-money';
import { invoiceLineDescriptions } from '@/utils/invoice-line-description';
import { recipientAddress } from '@/utils/postal-address';

const fmtDate = (ms: number | undefined): string => formatBusinessDateUS(ms);
const fmtMoney = (c: number): string => formatMoneyCents(c).replace(/^\$/, '');

interface Props {
	id: string;
	/**
	 * Called once the print-bundle query settles (success or error) so the
	 * batch printer can wait for every slip to be ready before triggering a
	 * single `window.print()` dialog.
	 */
	onReady?: () => void;
}

/**
 * Printable invoice body — matches the envelope-window statement layout
 * (`app/print/statement/[id].tsx`) so a stack of these in the batch printer
 * looks identical to a stack of statements: org block, To/Regarding boxes,
 * remit-to, late-fee legal copy, contact strip. The ledger table is replaced
 * by the invoice's own line items, with a Balance Due summary box.
 */
export const InvoiceSlip: React.FC<Props> = ({ id, onReady }) => {
	const { user } = useAuth();
	const { data, isLoading, isError } = useQuery({
		queryKey: ['accounting', 'invoice', 'print-bundle', id],
		queryFn: () => accountingService.getInvoicePrintBundle(id),
		enabled: !!id,
		retry: 1,
		staleTime: 60_000,
	});

	useEffect(() => {
		if (isLoading) return;
		if (!isError && !data) return;
		onReady?.();
	}, [isLoading, isError, data, onReady]);

	if (isLoading || !data) {
		return (
			<View style={styles.centered}>
				<ActivityIndicator />
				<RNText style={styles.muted}>Loading invoice…</RNText>
			</View>
		);
	}

	const { invoice, account, association: assoc, organization: org, holders } = data as InvoicePrintBundle;
	if (!account) {
		return (
			<View style={styles.centered}>
				<RNText style={styles.muted}>Invoice account missing.</RNText>
			</View>
		);
	}

	const orgCity = [org?.city, org?.state].filter(Boolean).join(', ');
	const orgCityZip = [orgCity, org?.zip].filter(Boolean).join(' ');
	const headerLines: string[] = [
		assoc?.name ?? '',
		org?.name ? `c/o ${org.name}` : '',
		org?.address ?? '',
		orgCityZip,
	].filter(Boolean);

	const ownerName = (() => {
		// Same rule as formatOwnerName: an emergency contact is never addressed (#569).
		const owners = holders.filter((h) => h.role === 'owner');
		const list = owners.length > 0 ? owners : holders.filter((h) => h.role !== 'emergency_contact');
		if (list.length === 0) return account.externalName ?? '';
		const lastNames = new Set(list.map((p) => p.lastName ?? '').filter(Boolean));
		if (lastNames.size === 1 && list.length > 1) {
			const last = list[0].lastName;
			return `${list.map((p) => p.firstName).join(' & ')} ${last ?? ''}`.trim();
		}
		return list.map((p) => `${p.firstName} ${p.lastName ?? ''}`.trim()).join(' & ');
	})();

	// The same c/o + address block the statement and envelope print.
	const { coLine, addressLines } = recipientAddress(account);

	const propertyAddressOneLine = (account.address ?? '')
		.split(/\r?\n/)
		.map((s) => s.trim())
		.filter(Boolean)
		.join('; ');

	const remitAddress = (org?.remitAddress?.trim()
		? org.remitAddress.split(/\r?\n/).map((s) => s.trim()).filter(Boolean).join('; ')
		: [
			org?.address,
			[org?.city, org?.state].filter(Boolean).join(', '),
			org?.zip,
		].filter(Boolean).join('; ').replace(/;\s+,/g, ',')
	);
	const payeeName = assoc?.name ?? '';

	// The association's invoice copy, else its statement copy, else the default (#678).
	const { calloutText, legalText, fdcpaText } = invoiceCopy(assoc, org?.name, invoice);

	const printerEmail = assoc?.pmEmail ?? user?.email ?? org?.email ?? '';

	const charges = invoice.charges ?? [];
	const descriptions = invoiceLineDescriptions(charges);
	const balanceDue = Math.max(0, invoice.totalCents - invoice.paidCents);

	// The table body keeps a fixed height — MIN_TABLE_ROWS rows, summary rows included — so the
	// subtotal, Balance Due and everything below land in the same place on every short invoice.
	// The line items come first, then the terms call-out, and blank ruled rows fill whatever is
	// left (`invoice-filler` clips them). A long invoice outgrows the area and pushes the rest down.
	const MIN_TABLE_ROWS = 12;
	// Paid and Written off (#682) are separate summary rows: paidCents counts write-offs as settled.
	const writtenOffCents = invoice.writtenOffCents ?? 0;
	const summaryRowCount = 1 + (invoice.paidCents - writtenOffCents > 0 ? 1 : 0) + (writtenOffCents > 0 ? 1 : 0);
	const lineAreaMinHeight = (MIN_TABLE_ROWS - summaryRowCount) * PRINT_ROW_HEIGHT;

	return (
		<View style={styles.sheet} {...({ dataSet: { printSlip: '1', printSheet: '1' } } as any)}>
			<FoldTicks boxTopIn={INVOICE_SHEET_TOP_IN} sidePadIn={INVOICE_SHEET_SIDE_PAD_IN} />
			<View style={styles.topRow} testID="print-header">
				<View style={styles.orgBlock}>
					{headerLines.map((line, i) => (
						<RNText
							key={i}
							style={i === 0 ? styles.orgFirstLine : styles.orgLine}
						>
							{line}
						</RNText>
					))}
				</View>
				<View style={styles.titleBlock}>
					<RNText style={styles.title}>Invoice</RNText>
					<View style={styles.stampStack}>
						<View style={styles.stampBox}>
							<RNText style={styles.stampLabel}>Invoice Date</RNText>
							<RNText style={styles.stampValue}>{fmtDate(invoice.issuedOn)}</RNText>
						</View>
						{invoice.invoiceNumber ? (
							<View style={styles.stampBox}>
								<RNText style={styles.stampLabel}>Invoice #</RNText>
								<RNText style={styles.stampValue}>{invoice.invoiceNumber}</RNText>
							</View>
						) : null}
						<View style={styles.stampBox}>
							<RNText style={styles.stampLabel}>Account #</RNText>
							<RNText style={styles.stampValue}>{account.accountNumber ?? account.id.slice(0, 8)}</RNText>
						</View>
						{invoice.dueOn ? (
							<View style={styles.stampBox}>
								<RNText style={styles.stampLabel}>Due Date</RNText>
								<RNText style={styles.stampValue}>{fmtDate(invoice.dueOn)}</RNText>
							</View>
						) : null}
					</View>
				</View>
			</View>

			<View style={styles.toAndCalloutRow} testID="print-to-row">
				<View style={styles.toBlock}>
					<View style={styles.toLabelBox}>
						<RNText style={styles.toLabel}>To</RNText>
					</View>
					<View style={styles.toBox}>
						<RNText style={[styles.toLine, styles.toName]}>{ownerName || '—'}</RNText>
						{coLine ? <RNText style={styles.toLine}>c/o {coLine}</RNText> : null}
						{addressLines.map((line, i) => (
							<RNText key={i} style={styles.toLine}>{line}</RNText>
						))}
					</View>
				</View>
				<View style={styles.calloutBlock}>
					<View style={styles.regardingLabelBox}>
						<RNText style={styles.regardingHeader}>Regarding</RNText>
					</View>
					<View style={styles.regardingValueBox}>
						<RNText style={styles.regardingValue}>{propertyAddressOneLine || '—'}</RNText>
					</View>
					<View style={styles.calloutBox}>
						<RNText style={styles.calloutText}>{calloutText}</RNText>
						{org?.website ? (
							<RNText style={styles.calloutWeb}>{org.website}</RNText>
						) : null}
					</View>
				</View>
			</View>

			{payeeName || remitAddress ? (
				<View style={styles.remitBox} {...({ dataSet: { printKeep: '1' } } as any)}>
					<RNText style={styles.remitText}>
						Payment may be made PAYABLE TO {'"'}{payeeName}{'"'} and
					</RNText>
					{remitAddress ? (
						<RNText style={styles.remitText}>mail to {remitAddress}</RNText>
					) : null}
				</View>
			) : null}

			<View style={styles.table}>
				<View style={[styles.row, styles.headRow]}>
					<RNText style={[styles.cell, styles.colDate, styles.headCell]}>Date</RNText>
					<RNText style={[styles.cell, styles.colTxn, styles.headCell]}>Description</RNText>
					<RNText style={[styles.cell, styles.colCat, styles.headCell]}>Type</RNText>
					<RNText style={[styles.cell, styles.colAmount, styles.headCell, styles.right]}>Amount</RNText>
				</View>

				<View style={[styles.lineArea, { minHeight: lineAreaMinHeight }]} testID="invoice-line-area">
					{charges.length === 0 ? (
						<View style={styles.row}>
							<RNText style={[styles.cell, styles.empty]}>No line items.</RNText>
						</View>
					) : null}

					{charges.map((c, i) => (
						<View
							key={c.id}
							style={styles.row}
							{...({ dataSet: { statementRow: '1' } } as any)}
						>
							<RNText style={[styles.cell, styles.colDate]}>{fmtDate(c.postedOn)}</RNText>
							<RNText style={[styles.cell, styles.colTxn]} numberOfLines={2}>{descriptions[i]}</RNText>
							<RNText style={[styles.cell, styles.colCat]}>{c.category}</RNText>
							<RNText style={[styles.cell, styles.colAmount, styles.right]}>{fmtMoney(c.amountCents)}</RNText>
						</View>
					))}

					{legalText.trim() ? (
						<View style={styles.termsBox} testID="invoice-terms" {...({ dataSet: { printKeep: '1' } } as object)}>
							<RNText style={styles.termsText}>{legalText}</RNText>
						</View>
					) : null}

					<View style={styles.filler} testID="invoice-filler">
						{Array.from({ length: MIN_TABLE_ROWS }).map((_, i) => (
							<View key={`pad-${i}`} style={styles.row}>
								<RNText style={[styles.cell, styles.colDate]}> </RNText>
								<RNText style={[styles.cell, styles.colTxn]}> </RNText>
								<RNText style={[styles.cell, styles.colCat]}> </RNText>
								<RNText style={[styles.cell, styles.colAmount, styles.right]}> </RNText>
							</View>
						))}
					</View>
				</View>

				<View style={[styles.row, styles.subtotalRow]}>
					<RNText style={[styles.cell, styles.colDate]}> </RNText>
					<RNText style={[styles.cell, styles.colTxn]}> </RNText>
					<RNText style={[styles.cell, styles.colCat, styles.right, styles.subtotalLabel]}>Subtotal</RNText>
					<RNText style={[styles.cell, styles.colAmount, styles.right, styles.subtotalLabel]}>{fmtMoney(invoice.totalCents)}</RNText>
				</View>
				{/* paidCents counts write-offs as settled (#682); the owner only PAID the rest. */}
				{invoice.paidCents - writtenOffCents > 0 ? (
					<View style={styles.row}>
						<RNText style={[styles.cell, styles.colDate]}> </RNText>
						<RNText style={[styles.cell, styles.colTxn]}> </RNText>
						<RNText style={[styles.cell, styles.colCat, styles.right]}>Paid</RNText>
						<RNText style={[styles.cell, styles.colAmount, styles.right]}>−{fmtMoney(invoice.paidCents - writtenOffCents)}</RNText>
					</View>
				) : null}
				{writtenOffCents > 0 ? (
					<View style={styles.row}>
						<RNText style={[styles.cell, styles.colDate]}> </RNText>
						<RNText style={[styles.cell, styles.colTxn]}> </RNText>
						<RNText style={[styles.cell, styles.colCat, styles.right]}>Written off</RNText>
						<RNText style={[styles.cell, styles.colAmount, styles.right]}>−{fmtMoney(writtenOffCents)}</RNText>
					</View>
				) : null}
			</View>

			<View style={styles.balanceRow}>
				<View style={{ flex: 1 }} />
				<View style={styles.balanceBox}>
					<RNText style={styles.balanceLabel}>Balance Due</RNText>
					<View style={styles.balanceValueBox}>
						<RNText style={styles.balanceValue}>{fmtMoney(balanceDue)}</RNText>
					</View>
				</View>
			</View>

			{invoice.memo ? (
				<View style={styles.memo}>
					<RNText style={styles.memoLabel}>Memo</RNText>
					<RNText style={styles.memoText}>{invoice.memo}</RNText>
				</View>
			) : null}

			<RNText style={styles.legalNotice}>{fdcpaText}</RNText>

			<View style={styles.contactTable} {...({ dataSet: { printKeep: '1' } } as any)}>
				<View style={styles.contactRow}>
					<View style={styles.contactHeadCell}><RNText style={styles.contactLabel}>Phone #</RNText></View>
					<View style={styles.contactHeadCell}><RNText style={styles.contactLabel}>Fax #</RNText></View>
					<View style={[styles.contactHeadCell, { flex: 2 }]}><RNText style={styles.contactLabel}>E-Mail</RNText></View>
					<View style={[styles.contactHeadCell, styles.lastCell]}><RNText style={styles.contactLabel}>Web Site</RNText></View>
				</View>
				<View style={[styles.contactRow, styles.contactValueRow]}>
					<View style={styles.contactValueCell}><RNText style={styles.contactValue}>{org?.phone ?? ''}</RNText></View>
					<View style={styles.contactValueCell}><RNText style={styles.contactValue}>{org?.fax ?? ''}</RNText></View>
					<View style={[styles.contactValueCell, { flex: 2 }]}><RNText style={styles.contactValue}>{printerEmail}</RNText></View>
					<View style={[styles.contactValueCell, styles.lastCell]}><RNText style={styles.contactValue}>{org?.website ?? ''}</RNText></View>
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	centered: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
	muted: { color: '#90a4ae', marginTop: 8 },
	sheet: {
		width: '100%', maxWidth: 950, alignSelf: 'center',
		backgroundColor: '#fff',
		paddingVertical: 28, paddingHorizontal: 32,
		borderRadius: 4, borderWidth: 1, borderColor: '#cfd8dc',
	},
	// Fixed minimum height so the To row lands on the envelope window (ENVELOPE.toTopIn) however
	// tall the org lines, title and stamps render.
	topRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', minHeight: headerMinHeight(INVOICE_SHEET_TOP_IN) },
	orgBlock: { flex: 1, paddingRight: 16 },
	orgFirstLine: { fontSize: PRINT_FONT.orgFirstLine, lineHeight: PRINT_LINE.orgFirstLine, fontWeight: '700', color: '#000' },
	orgLine: { fontSize: PRINT_FONT.orgLine, lineHeight: PRINT_LINE.orgLine, color: '#000' },
	titleBlock: { alignItems: 'flex-end', minWidth: 240 },
	title: { fontSize: PRINT_FONT.title, lineHeight: PRINT_LINE.title, fontWeight: '700', color: '#000', marginBottom: 8 },
	stampStack: { width: 220 },
	stampBox: {
		flexDirection: 'row', alignItems: 'center',
		borderWidth: 1, borderColor: '#000',
		paddingVertical: PRINT_BOX_ROW_PAD, paddingHorizontal: 8,
		marginTop: -1,
	},
	stampLabel: { fontSize: PRINT_FONT.stampLabel, lineHeight: PRINT_LINE.boxRow, fontWeight: '700', color: '#000', flex: 1 },
	stampValue: { fontSize: PRINT_FONT.stampValue, lineHeight: PRINT_LINE.boxRow, color: '#000', textAlign: 'right' },
	toAndCalloutRow: { flexDirection: 'row', marginTop: TO_ROW_GAP, gap: 16 },
	toBlock: { flex: 1 },
	toLabelBox: {
		borderWidth: 1, borderColor: '#000',
		paddingVertical: PRINT_BOX_ROW_PAD, paddingHorizontal: 8,
		alignItems: 'center', justifyContent: 'center',
		backgroundColor: '#f5f5f5',
	},
	toLabel: { fontSize: PRINT_FONT.boxLabel, lineHeight: PRINT_LINE.boxRow, color: '#000', fontWeight: '700', textAlign: 'center' },
	toBox: {
		borderWidth: 1, borderColor: '#000', borderTopWidth: 0,
		// The window clips from the bottom, so the bottom padding stays at its pre-scale 10px: the
		// last address line ends exactly where it did before the type scale went up, and a 4-line
		// address still fills the same 73px box. The extra height comes out of the top padding.
		paddingTop: 6, paddingBottom: 10, paddingHorizontal: 10, minHeight: 70,
		alignItems: 'flex-start', justifyContent: 'center',
	},
	// Indented inside the box (whose size and border don't change) so the address sits nicer in the window.
	toLine: {
		fontSize: PRINT_FONT.address, lineHeight: PRINT_LINE.address, color: '#000', textAlign: 'left',
		paddingLeft: TO_ADDRESS_INDENT,
	},
	toName: { fontWeight: '700' },
	calloutBlock: { flex: 1.2 },
	regardingLabelBox: {
		borderWidth: 1, borderColor: '#000',
		paddingVertical: PRINT_BOX_ROW_PAD, paddingHorizontal: 8,
		alignItems: 'center', justifyContent: 'center',
		backgroundColor: '#f5f5f5',
	},
	regardingHeader: { fontSize: PRINT_FONT.boxLabel, lineHeight: PRINT_LINE.boxRow, fontWeight: '700', color: '#000', textAlign: 'center' },
	regardingValueBox: {
		borderWidth: 1, borderColor: '#000', borderTopWidth: 0,
		paddingVertical: 6, paddingHorizontal: 8,
		alignItems: 'center', justifyContent: 'center',
	},
	regardingValue: { fontSize: PRINT_FONT.regarding, color: '#000', textAlign: 'center' },
	calloutBox: {
		backgroundColor: '#fff59d', borderWidth: 1, borderColor: '#000',
		padding: 8, minHeight: 50, marginTop: 6,
	},
	calloutText: { fontSize: PRINT_FONT.callout, color: '#000' },
	calloutWeb: { fontSize: PRINT_FONT.callout, color: '#000', fontWeight: '600', marginTop: 4, textAlign: 'center' },
	remitBox: {
		borderWidth: 1.5, borderColor: '#1b5e20',
		paddingVertical: 10, paddingHorizontal: 14,
		marginTop: 14,
		alignItems: 'center', justifyContent: 'center',
	},
	remitText: { fontSize: PRINT_FONT.remit, fontWeight: '700', color: '#1b5e20', textAlign: 'center' },
	table: { width: '100%', marginTop: 18, borderTopWidth: 1, borderColor: '#000' },
	row: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 2.5, borderBottomWidth: 0.5, borderColor: '#cfd8dc' },
	lineArea: { flexDirection: 'column' },
	termsBox: {
		backgroundColor: '#fff9c4', borderWidth: 1.5, borderColor: '#000',
		paddingVertical: 10, paddingHorizontal: 14, marginVertical: 8,
	},
	termsText: { fontSize: PRINT_FONT.terms, lineHeight: PRINT_LINE.terms, fontWeight: '600', color: '#000' },
	// Blank ruled rows under the terms, clipped to the space the fixed table height leaves. A 0px
	// basis, not `flex: 1`'s 0%: the area's height is only a minimum, so a percentage basis would
	// resolve to the rows' full content height and never shrink.
	filler: { flexGrow: 1, flexShrink: 1, flexBasis: 0, overflow: 'hidden' },
	headRow: { borderBottomWidth: 1.5, borderColor: '#000', paddingVertical: 4 },
	cell: { fontSize: PRINT_FONT.cell, lineHeight: PRINT_LINE.cell, color: '#000', paddingHorizontal: 4 },
	// 14, not the cell's 15.5: the header row keeps its pre-pin height so the subtotal doesn't move.
	headCell: { fontWeight: '700', fontSize: PRINT_FONT.cell, lineHeight: 14 },
	colDate: { width: 80 },
	colTxn: { flex: 1, minWidth: 0 },
	colCat: { width: 110 },
	colAmount: { width: 100 },
	right: { textAlign: 'right' },
	empty: { color: '#78909c', fontStyle: 'italic', textAlign: 'center', padding: 12, flex: 1 },
	subtotalRow: { borderTopWidth: 1, borderColor: '#000', borderBottomWidth: 0, marginTop: 2 },
	subtotalLabel: { fontWeight: '700' },
	balanceRow: { flexDirection: 'row', marginTop: 16, alignItems: 'center' },
	balanceBox: { flexDirection: 'row', alignItems: 'center' },
	balanceLabel: { fontSize: PRINT_FONT.balanceLabel, fontWeight: '700', color: '#000', marginRight: 8 },
	balanceValueBox: {
		backgroundColor: '#fff59d', borderWidth: 1, borderColor: '#000',
		paddingVertical: 4, paddingHorizontal: 14, minWidth: 110, alignItems: 'flex-end',
	},
	balanceValue: { fontSize: PRINT_FONT.balanceValue, fontWeight: '700', color: '#000' },
	memo: { marginTop: 12, paddingVertical: 6, paddingHorizontal: 8, backgroundColor: '#fafafa', borderLeftWidth: 3, borderLeftColor: '#90a4ae' },
	memoLabel: { fontSize: PRINT_FONT.memoLabel, fontWeight: '700', color: '#607d8b', textTransform: 'uppercase' },
	memoText: { fontSize: PRINT_FONT.memoText, color: '#212121' },
	legalNotice: { fontSize: PRINT_FONT.legal, color: '#000', marginTop: 18, lineHeight: PRINT_LINE.legal, fontStyle: 'italic' },
	contactTable: { borderWidth: 1, borderColor: '#000', marginTop: 8 },
	contactRow: { flexDirection: 'row' },
	contactValueRow: { borderTopWidth: 1, borderColor: '#000' },
	contactHeadCell: {
		flex: 1, paddingVertical: 4, paddingHorizontal: 6,
		borderRightWidth: 1, borderColor: '#000',
		alignItems: 'center', justifyContent: 'center',
		backgroundColor: '#f5f5f5',
	},
	contactValueCell: {
		flex: 1, paddingVertical: 5, paddingHorizontal: 6,
		borderRightWidth: 1, borderColor: '#000',
		alignItems: 'center', justifyContent: 'center',
	},
	lastCell: { borderRightWidth: 0 },
	contactLabel: { fontSize: PRINT_FONT.contactLabel, fontWeight: '700', color: '#000', textAlign: 'center' },
	contactValue: { fontSize: PRINT_FONT.contactValue, color: '#000', textAlign: 'center' },
});
